import { isApiError } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, Skeleton, toast } from "@sagot/ui";
import { useEffect, useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useFormatters } from "../shared/format";
import { useCustomerContext, useRevealReference } from "../shared/customer-context/customer-context-api";
import { REVEAL_VISIBLE_MS, type CustomerContext } from "../shared/customer-context/customer-context-model";
import { AccountStatusChip, PersonalMark, SourceChip, TierChip, useFieldText } from "../shared/customer-context/customer-context-parts";
import { LookupLog } from "./lookup-log";

interface CustomerContextCardProps {
  conversationId: string;
  endUserId: string;
}

function CardSkeleton() {
  const { t } = useTranslation();
  return (
    <div role="status" data-testid="customer-card-skeleton" className="space-y-3 rounded-lg border border-line p-3">
      <span className="sr-only">{t("customerContext.card.loading")}</span>
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-5 w-full" />
      <Skeleton className="h-5 w-3/4" />
    </div>
  );
}

/** Verified-by line: "Verified by operator · 08:12", or "Stale · last update 08:12" when it is over a day old. */
function VerifiedBadge({ context }: { context: CustomerContext }) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  if (!context.as_of) return <Badge tone="neutral">{t("customerContext.card.noContext")}</Badge>;
  if (context.stale) {
    return (
      <Badge tone="warning" data-testid="customer-stale">
        <Icon name="alert-triangle" className="size-3" />
        {t("customerContext.card.staleAt", { time: fmt.dateTime(context.as_of) })}
      </Badge>
    );
  }
  const key = context.verified_by === "staff" ? "customerContext.card.setByStaff" : "customerContext.card.verifiedBy";
  return (
    <Badge tone="success" data-testid="customer-verified">
      <Icon name="shield-check" className="size-3" />
      {t(key, { time: fmt.time(context.as_of) })}
    </Badge>
  );
}

/** The masked reference, with a Reveal that needs `customers.view_reference` and hides itself again. */
function ReferenceRow({ context }: { context: CustomerContext }) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const reveal = useRevealReference(context.end_user_id);
  const [shown, setShown] = useState<string | null>(null);

  useEffect(() => {
    if (shown === null) return;
    const timer = setTimeout(() => { setShown(null); }, REVEAL_VISIBLE_MS);
    return () => { clearTimeout(timer); };
  }, [shown]);

  const run = () => {
    reveal.mutate(undefined, {
      onSuccess: (data) => { setShown(data.ref); },
      onError: (error) => { toast.error(errorMessage(error)); },
    });
  };

  return (
    <div className="col-span-2">
      <dt className="text-fg-muted">{t("customerContext.card.reference")}</dt>
      <dd className="mt-0.5 flex flex-wrap items-center gap-2">
        <span className="truncate font-mono" data-testid="customer-ref">
          {shown ?? context.ref_masked}
        </span>
        {context.can_reveal ? (
          shown ? (
            <Button variant="ghost" size="sm" aria-label={t("customerContext.reveal.hideLabel")} onClick={() => { setShown(null); }}>
              <Icon name="eye-off" />
              {t("customerContext.reveal.hide")}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" loading={reveal.isPending} aria-label={t("customerContext.reveal.buttonLabel")} onClick={run}>
              <Icon name="eye" />
              {t("customerContext.reveal.button")}
            </Button>
          )
        ) : null}
      </dd>
    </div>
  );
}

function FieldRows({ context }: { context: CustomerContext }) {
  const text = useFieldText();
  return (
    <>
      {context.fields.map((field) => (
        <div key={field.key} className="col-span-2 min-w-0" data-testid="customer-field">
          <dt className="text-fg-muted">
            {field.label} {field.pii ? <PersonalMark /> : null}
          </dt>
          <dd className="mt-0.5 min-w-0">
            <span className="block break-words font-semibold">{text(field.value)}</span>
            <span className="mt-1 inline-block max-w-full">
              <SourceChip source={field.source} asOf={field.as_of} />
            </span>
          </dd>
        </div>
      ))}
    </>
  );
}

function CardBody({ context, conversationId }: { context: CustomerContext; conversationId: string }) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="customer-card-title" className="space-y-3 rounded-lg border border-line bg-surface-2 p-3" data-testid="customer-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="customer-card-title" className="text-xs font-bold">
          {t("customerContext.card.title")}
        </h3>
        <VerifiedBadge context={context} />
      </div>
      {context.stale ? <p className="text-xs text-warning">{t("customerContext.card.staleHint")}</p> : null}
      <dl className="grid grid-cols-2 gap-3 text-xs">
        <ReferenceRow key={context.end_user_id} context={context} />
        <div>
          <dt className="text-fg-muted">{t("customerContext.card.tier")}</dt>
          <dd className="mt-0.5">
            <TierChip tier={context.tier} />
          </dd>
        </div>
        <div>
          <dt className="text-fg-muted">{t("customerContext.card.status")}</dt>
          <dd className="mt-0.5">
            <AccountStatusChip status={context.account_status} />
          </dd>
        </div>
        <FieldRows context={context} />
      </dl>
      <p className="text-[11px] text-fg-muted">{context.can_reveal ? t("customerContext.reveal.audited") : t("customerContext.reveal.needsPermission")}</p>
      <LookupLog conversationId={conversationId} />
    </section>
  );
}

/** Inbox pin 3: the verified customer beside the chat. Updates by itself within seconds of a change. */
export function CustomerContextCard({ conversationId, endUserId }: CustomerContextCardProps) {
  const { t } = useTranslation();
  const query = useCustomerContext(conversationId, endUserId);
  if (query.data) return <CardBody context={query.data} conversationId={conversationId} />;
  if (query.isPending) return <CardSkeleton />;
  // A member without a door to customer details sees none of it (no card, no error).
  if (isApiError(query.error) && (query.error.status === 403 || query.error.status === 404)) return null;
  return (
    <div role="alert" className="space-y-2 rounded-lg border border-line bg-danger-soft p-3 text-xs" data-testid="customer-card-error">
      <p className="font-bold text-danger">{t("customerContext.card.errorTitle")}</p>
      <p className="text-fg-muted">{t("customerContext.card.errorBody")}</p>
      <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
        <Icon name="refresh-cw" />
        {t("customerContext.card.retry")}
      </Button>
    </div>
  );
}
